/**
 * Actions Column Pattern
 * 
 * Creates action columns with buttons and standard CRUD operations.
 */

import React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { TABLE_SETTINGS } from '../../constants/tableSettings';
import { getActionColumnConfig, type ActionDefinition } from '../tableHelpers';
import type { ActionsColumnOptions } from './types';

/**
 * Creates an actions column with custom action definitions
 * 
 * @param actions - Array of action definitions
 * @param options - Optional customization options
 * @returns ColumnDef for TanStack Table
 * 
 * @example
 * actionsColumn([
 *   {
 *     id: 'edit',
 *     label: 'Edit User',
 *     icon: <EditIcon />,
 *     onClick: (user) => handleEdit(user),
 *     intent: 'outline'
 *   },
 *   {
 *     id: 'delete',
 *     label: 'Delete User',
 *     icon: <TrashIcon />,
 *     onClick: (user) => handleDelete(user),
 *     intent: 'danger'
 *   }
 * ])
 */
export function actionsColumn<T>(
  actions: ActionDefinition<T>[],
  options: Partial<ActionsColumnOptions> = {}
): ColumnDef<T> {
  const {
    header = 'Actions',
    width = 120,
  } = options;

  // Get base action column config
  const baseConfig = getActionColumnConfig<T>();

  return {
    ...baseConfig,
    header,
    size: width,
    cell: ({ row }) => {
      const item = row.original;
      
      // Filter actions based on hidden property
      const visibleActions = actions.filter(action => !action.hidden?.(item));
      
      // Return proper React elements for functional action buttons
      return React.createElement('div', {
        className: 'flex items-center gap-2'
      }, 
      // Create array of button elements as children
      visibleActions.map((action) => 
        React.createElement('button', {
          key: action.id,
          onClick: () => action.onClick(item),
          disabled: action.disabled?.(item),
          className: `p-1 rounded transition-colors ${
            action.intent === 'danger' 
              ? action.disabled?.(item) 
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-red-600 hover:text-red-800'
              : action.intent === 'secondary'
                ? action.disabled?.(item)
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-green-600 hover:text-green-800'
                : action.disabled?.(item)
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'text-blue-600 hover:text-blue-800'
          }`,
          title: action.label
        }, action.icon) // icon is already a React element
      ));
    },
  };
}

/**
 * Creates a standard CRUD actions column (Edit + Delete)
 * 
 * @param onEdit - Edit action handler
 * @param onDelete - Delete action handler
 * @param options - Optional customization
 * @returns ColumnDef for TanStack Table
 */
export function standardActionsColumn<T>(
  onEdit: (item: T) => void,
  onDelete: (item: T) => void,
  options: {
    canEdit?: (item: T) => boolean;
    canDelete?: (item: T) => boolean;
    confirmDelete?: boolean;
  } = {}
): ColumnDef<T> {
  const {
    canEdit = () => true,
    canDelete = () => true,
    confirmDelete = TABLE_SETTINGS.actions.confirmDestructive,
  } = options;

  const actions: ActionDefinition<T>[] = [
    {
      id: 'edit',
      label: 'Edit',
      icon: 'edit', // Will be converted to proper icon later
      onClick: onEdit,
      intent: 'outline',
      hidden: (item) => !canEdit(item),
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: 'trash', // Will be converted to proper icon later
      onClick: (item) => {
        if (confirmDelete) {
          const confirmed = window.confirm(`Are you sure you want to delete this item?`);
          if (confirmed) {
            onDelete(item);
          }
        } else {
          onDelete(item);
        }
      },
      intent: 'danger',
      hidden: (item) => !canDelete(item),
    },
  ];

  return actionsColumn(actions);
}

/**
 * Creates a simple edit-only actions column
 */
export function editActionColumn<T>(
  onEdit: (item: T) => void,
  canEdit: (item: T) => boolean = () => true
): ColumnDef<T> {
  const actions: ActionDefinition<T>[] = [
    {
      id: 'edit',
      label: 'Edit',
      icon: 'edit',
      onClick: onEdit,
      intent: 'outline',
      hidden: (item) => !canEdit(item),
    },
  ];

  return actionsColumn(actions, { width: 80 });
}

/**
 * Creates a view-only actions column
 */
export function viewActionColumn<T>(
  onView: (item: T) => void,
  canView: (item: T) => boolean = () => true
): ColumnDef<T> {
  const actions: ActionDefinition<T>[] = [
    {
      id: 'view',
      label: 'View',
      icon: 'eye',
      onClick: onView,
      intent: 'outline',
      hidden: (item) => !canView(item),
    },
  ];

  return actionsColumn(actions, { width: 80 });
} 